import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, MapPin, ArrowUpRight, Search, FileText } from "lucide-react";
import { motion } from "framer-motion";
import { getApiBaseUrl } from "../config";
import "./JobsList.css";

const API_URL = getApiBaseUrl();

const EMPLOYMENT_LABEL = {
  FULL_TIME: "Full-time",
  PART_TIME: "Part-time",
  CONTRACTOR: "Contract",
  TEMPORARY: "Temporary",
  INTERN: "Internship",
  OTHER: "Other",
};

function jobUrl(country) {
  if (!country) return null;
  const map = {
    "saudi arabia": "https://en.wikipedia.org/wiki/Saudi_Arabia",
    "united arab emirates": "https://en.wikipedia.org/wiki/United_Arab_Emirates",
    qatar: "https://en.wikipedia.org/wiki/Qatar",
    oman: "https://en.wikipedia.org/wiki/Oman",
    kuwait: "https://en.wikipedia.org/wiki/Kuwait",
    bahrain: "https://en.wikipedia.org/wiki/Bahrain",
  };
  return map[country.toLowerCase()] || null;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function JobsList() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [industry, setIndustry] = useState("all");
  const [country, setCountry] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/jobs`);
        if (!res.ok) throw new Error("Couldn't load openings");
        const data = await res.json();
        if (!cancelled) {
          setJobs(data.jobs || []);
          setError(null);
        }
      } catch (err) {
        // Fall through to empty state so the page stays useful when the API is
        // briefly unreachable. The empty UI nudges visitors to submit a CV.
        if (typeof console !== "undefined") console.warn("[jobs] fetch failed:", err);
        if (!cancelled) {
          setJobs([]);
          setError(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const industries = useMemo(() => {
    const s = new Set(jobs.map((j) => j.industry).filter(Boolean));
    return ["all", ...Array.from(s).sort()];
  }, [jobs]);

  const countries = useMemo(() => {
    const s = new Set(jobs.map((j) => j.country).filter(Boolean));
    return ["all", ...Array.from(s).sort()];
  }, [jobs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return jobs.filter((j) => {
      if (industry !== "all" && j.industry !== industry) return false;
      if (country !== "all" && j.country !== country) return false;
      if (q) {
        const hay = `${j.title} ${j.industry} ${j.country} ${j.city || ""} ${j.summary || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [jobs, industry, country, search]);

  return (
    <section className="jobs">
      <div className="container">
        <div className="jobs__head">
          <span className="eyebrow">Active openings</span>
          <h1 className="jobs__title">
            Roles open now across{" "}
            <span className="jobs__title-accent">the Gulf.</span>
          </h1>
          <p className="jobs__intro">
            Open positions placed by Adel Group. New roles appear here as
            employers confirm. If nothing fits today, submit your CV and we'll
            keep your profile on file for future placements.
          </p>
        </div>

        {!loading && jobs.length > 0 && (
          <div className="jobs__filters">
            <label className="jobs__search">
              <Search size={16} aria-hidden="true" />
              <input
                type="search"
                placeholder="Search by title, industry, country…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search jobs"
              />
            </label>
            <select
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              aria-label="Filter by industry"
              className="jobs__select"
            >
              {industries.map((i) => (
                <option key={i} value={i}>
                  {i === "all" ? "All industries" : i}
                </option>
              ))}
            </select>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              aria-label="Filter by country"
              className="jobs__select"
            >
              {countries.map((c) => (
                <option key={c} value={c}>
                  {c === "all" ? "All countries" : c}
                </option>
              ))}
            </select>
          </div>
        )}

        {loading ? (
          <div className="jobs__state" role="status" aria-live="polite">
            <div className="jobs__spinner" aria-hidden="true" />
            <p>Loading openings…</p>
          </div>
        ) : error ? (
          <div className="jobs__state jobs__state--error">
            <p>{error}</p>
            <p className="jobs__state-sub">
              Please try again, or{" "}
              <Link to="/contact">contact us directly</Link>.
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="jobs__empty">
            <Briefcase size={28} strokeWidth={1.4} />
            <h2 className="jobs__empty-title">
              {jobs.length === 0
                ? "No active openings right now."
                : "No roles match these filters."}
            </h2>
            <p className="jobs__empty-sub">
              {jobs.length === 0
                ? "Submit your CV anyway — when employers confirm new positions, we match against your profile first."
                : "Try clearing a filter, or send us your CV and we'll keep it on file."}
            </p>
            <div className="jobs__empty-actions">
              <Link to="/job-seekers" className="jobs__cta jobs__cta--primary">
                <FileText size={16} />
                Submit your CV
              </Link>
              <Link to="/contact" className="jobs__cta jobs__cta--ghost">
                Contact our team
                <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        ) : (
          <ol className="jobs__list">
            {filtered.map((job, i) => {
              const flagUrl = jobUrl(job.country);
              return (
                <motion.li
                  key={job.id}
                  className="jobs__card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{
                    duration: 0.5,
                    delay: i * 0.04,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                >
                  <div className="jobs__card-head">
                    <span className="jobs__card-tag">{job.industry}</span>
                    <span className="jobs__card-date">
                      Posted {fmtDate(job.posted_at)}
                    </span>
                  </div>
                  <h3 className="jobs__card-title">{job.title}</h3>
                  <p className="jobs__card-summary">{job.summary}</p>
                  <div className="jobs__card-meta">
                    <span className="jobs__card-meta-item">
                      <MapPin size={14} strokeWidth={1.8} />
                      {job.city ? `${job.city}, ` : ""}
                      {flagUrl ? (
                        <a
                          href={flagUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="jobs__card-country"
                        >
                          {job.country}
                        </a>
                      ) : (
                        job.country
                      )}
                    </span>
                    <span className="jobs__card-meta-item">
                      <Briefcase size={14} strokeWidth={1.8} />
                      {EMPLOYMENT_LABEL[job.employment_type] || job.employment_type}
                    </span>
                  </div>
                  <div className="jobs__card-actions">
                    {job.apply_url ? (
                      <a
                        href={job.apply_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="jobs__cta jobs__cta--primary"
                      >
                        Apply <ArrowUpRight size={14} />
                      </a>
                    ) : (
                      <Link
                        to="/job-seekers"
                        className="jobs__cta jobs__cta--primary"
                      >
                        Apply via CV <ArrowUpRight size={14} />
                      </Link>
                    )}
                    <Link to="/contact" className="jobs__cta jobs__cta--ghost">
                      Ask a question
                    </Link>
                  </div>
                </motion.li>
              );
            })}
          </ol>
        )}
      </div>
    </section>
  );
}
